// src/components/MinistryReportsTest.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  getPriceDS,
  createOrderDS,
  verifyAndGenerateDS,
  getReportDownloadUrl,
  searchMinistryEntities,
  getMinistryFilterAvailability,
  generateReportWithCombo,
} from '../tmApi';
import Layout from './Layout';
import './TestReportsCommon.css';

import { auth } from '../firebaseConfig';
import { consumeQuota } from '../quotaManager';

// tiny inline spinner (no CSS needed)
function Spinner({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 50 50" role="status" aria-label="Loading">
      <circle cx="25" cy="25" r="20" fill="none" stroke="#94a3b8" strokeWidth="6" strokeOpacity="0.25" />
      <circle
        cx="25"
        cy="25"
        r="20"
        fill="none"
        stroke="#0ea5e9"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray="31.415,188.495"
        transform="rotate(0 25 25)"
      >
        <animateTransform
          attributeName="transform"
          type="rotate"
          from="0 25 25"
          to="360 25 25"
          dur="0.8s"
          repeatCount="indefinite"
        />
      </circle>
    </svg>
  );
}

export default function MinistryReportsTest() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  const [allItems, setAllItems] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadMsg, setLoadMsg] = useState('');

  const [selected, setSelected] = useState(null);
  const [slug, setSlug] = useState('');
  const [filters, setFilters] = useState(null);

  const [availability, setAvailability] = useState(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  const num = (n) => Number(n || 0).toLocaleString();
  const norm = (s) => (typeof s === 'string' ? s.trim() : '');

  function normalizeRow(r) {
    const type = r.type;
    const label = norm(r.name || r.label || r.path);
    let ministryName = '';
    if (type === 'ministry') ministryName = label;
    else {
      const parts = (r.path || '').split('—').map((x) => x.trim());
      ministryName = parts[parts.length - 1] || r.slug || '';
    }
    return {
      ...r,
      type,
      label,
      ministryName,
      dept: norm(r.dept),
      org: norm(r.org),
      rows_count: Number(r.rows_count || 0),
      available_min: r.available_min || (r.available && r.available.min) || '',
      available_max: r.available_max || (r.available && r.available.max) || '',
    };
  }

  function groupAndSort(items) {
    const m = new Map();
    for (const raw of items) {
      const r = normalizeRow(raw);
      if (!r.slug || !r.ministryName) continue;
      if (!m.has(r.ministryName)) m.set(r.ministryName, { header: null, depts: [], orgs: [] });
      const g = m.get(r.ministryName);
      if (r.type === 'ministry') g.header = r;
      else if (r.type === 'department') g.depts.push(r);
      else if (r.type === 'organisation') g.orgs.push(r);
    }
    return [...m.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], undefined, { sensitivity: 'base' }))
      .map(([ministryName, g]) => ({
        ministryName,
        header: g.header,
        depts: g.depts.sort((a, b) =>
          (a.dept || a.label).localeCompare(b.dept || b.label, undefined, { sensitivity: 'base' })
        ),
        orgs: g.orgs.sort((a, b) =>
          (a.org || a.label).localeCompare(b.org || b.label, undefined, { sensitivity: 'base' })
        ),
      }));
  }

  async function ensureListLoaded() {
    if (allItems.length || loadingList) return;
    setLoadingList(true);
    setLoadMsg('Loading ministry list…');
    try {
      const rows = await searchMinistryEntities('', 20000);
      setAllItems(Array.isArray(rows) ? rows : []);
      setLoadMsg('');
    } catch (e) {
      console.error(e);
      setLoadMsg('Could not load ministry hierarchy.');
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    function onDoc(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const visibleGroups = useMemo(() => {
    if (!q.trim()) return groupAndSort(allItems);
    const s = q.trim().toLowerCase();
    const filtered = allItems.filter((r) => {
      const n = normalizeRow(r);
      return (
        (n.label && n.label.toLowerCase().includes(s)) ||
        (n.dept && n.dept.toLowerCase().includes(s)) ||
        (n.org && n.org.toLowerCase().includes(s)) ||
        (n.ministryName && n.ministryName.toLowerCase().includes(s)) ||
        (n.slug && n.slug.toLowerCase().includes(s))
      );
    });
    return groupAndSort(filtered);
  }, [allItems, q]);

  // selection
  async function pick(selRaw) {
    const sel = normalizeRow(selRaw);
    setSelected(sel);
    setPricing(null);
    setDownloadUrl('');
    setMessage('');
    setOpen(false);
    setQ(sel.path || sel.label || sel.ministryName || sel.name || '');

    // always ministry slug
    setSlug(sel.slug || '');

    // clear dates while fetching fresh availability
    setFrom('');
    setTo('');
    setAvailability(null);
    setAvailabilityLoading(true);

    // build filters
    if (sel.type === 'department' && sel.dept) {
      setFilters({ departments: [sel.dept] });
    } else if (sel.type === 'organisation' && sel.org) {
      setFilters({ organisations: [sel.org] });
    } else {
      setFilters(null);
    }

    try {
      if (sel.type === 'ministry') {
        const a = { min: sel.available_min || '', max: sel.available_max || '' };
        setAvailability(a);
        setFrom(a.min || '');
        setTo(a.max || '');
      } else {
        const ftype = sel.type === 'department' ? 'department' : 'organisation';
        const value = ftype === 'department' ? sel.dept : sel.org;
        const a = await getMinistryFilterAvailability(sel.slug, ftype, value);
        const min = a.min || sel.available_min || '';
        const max = a.max || sel.available_max || '';
        setAvailability({ min, max });
        setFrom(min || '');
        setTo(max || '');
      }
    } catch (e) {
      console.error(e);
      const fallback = { min: sel.available_min || '', max: sel.available_max || '' };
      setAvailability(fallback);
      setFrom(fallback.min || '');
      setTo(fallback.max || '');
    } finally {
      setAvailabilityLoading(false);
    }
  }

  async function handlePrice() {
    if (!slug || !from || !to) return;
    setBusy(true);
    setMessage('');
    setPricing(null);
    try {
      const p = await getPriceDS({
        dataset: 'ministry',
        slug,
        date_from: from,
        date_to: to,
        filters: filters || undefined,
      });
      setPricing(p);
    } catch (e) {
      console.error(e);
      setMessage(`Pricing failed: ${e.message || 'Error'}`);
    } finally {
      setBusy(false);
    }
  }

  async function handlePay() {
    if (!pricing) return;
    setBusy(true);
    setMessage('');
    setDownloadUrl('');
    try {
      const order = await createOrderDS({
        dataset: 'ministry',
        slug,
        date_from: from,
        date_to: to,
        filters: filters || undefined,
      });

      // 2) Best-effort prefill from Firebase (optional)
      let contact = '';
      let email = '';
      try {
        const { getAuth } = await import('firebase/auth'); // dynamic import so it won't bundle-break
        const u = getAuth().currentUser;
        contact = u?.phoneNumber || '';
        email = u?.email || '';
      } catch {}

      // 3) Detect iOS Safari to avoid upi:// deep-link (causes “address is invalid”)
      const ua = navigator.userAgent || '';
      const isIOS = /iP(hone|ad|od)/.test(ua);
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const iOSSafari = isIOS && isSafari;

      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Tender Managers',
        description: selected
          ? selected.label || selected.path || selected.name
          : 'Ministry report',
        order_id: order.order_id,

        // ✅ Don’t ask phone/email again in Checkout
        prefill: { contact, email },

        // ✅ iOS Safari: force UPI Collect instead of app-intent deep links
        ...(iOSSafari ? { config: { upi: { flow: 'collect' } } } : {}),
        handler: async (response) => {
          try {
            const result = await verifyAndGenerateDS({
              dataset: 'ministry',
              slug,
              date_from: from,
              date_to: to,
              filters: filters || undefined,
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            const d = await getReportDownloadUrl(result.purchaseId);
            setDownloadUrl(d.url);
            setMessage('Report is ready ✅');
          } catch (err) {
            console.error(err);
            setMessage('Verification failed');
          }
        },
        theme: { color: '#0ea5e9' },
        modal: { ondismiss: () => setMessage('Payment window closed') },
      });
      rzp.open();
    } catch (e) {
      console.error(e);
      setMessage('Could not create order');
    } finally {
      setBusy(false);
    }
  }

  // Use plan quota (no Razorpay)
  async function handleQuotaGenerate() {
    if (!pricing) { setMessage('Please click "Get Price" first.'); return; }
    if (!slug || !from || !to) { setMessage('Select ministry/department/organisation and date range first.'); return; }

    const rowsNeeded = pricing.rows || 0;
    if (!rowsNeeded) { setMessage('Could not determine rows for this report.'); return; }

    setBusy(true);
    setMessage('');
    setDownloadUrl('');

    try {
      const u = auth.currentUser;
      if (!u || !u.phoneNumber) { setMessage('Please login to use plan quota.'); setBusy(false); return; }

      const result = await consumeQuota('reportRows', rowsNeeded);
      if (!result.ok) {
        setMessage(`${result.reason} You can still use Pay & Generate instead.`);
        setBusy(false);
        return;
      }

      const gen = await generateReportWithCombo({
        dataset: 'ministry', slug, date_from: from, date_to: to,
        filters: filters || undefined, rows: rowsNeeded,
      });
      const d = await getReportDownloadUrl(gen.purchaseId);
      setDownloadUrl(d.url);
      setMessage('Report generated using your plan quota.');
    } catch (e) {
      console.error(e);
      setMessage('Could not generate report using plan quota.');
    } finally {
      setBusy(false);
    }
  }

  const disabledPrice = !slug || !from || !to || busy || availabilityLoading;
  const disabledPay = !pricing || busy || availabilityLoading;
  const disabledQuota = !pricing || busy || availabilityLoading;

  return (
    <Layout>
      <div style={{ maxWidth: 900, margin: '24px auto', padding: 16 }}>
        <h2>Ministry Reports</h2>

        <div ref={wrapRef} style={{ position: 'relative', marginBottom: 12 }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}>
            Ministry / Department / Organisation
          </label>

          <div
            onClick={() => {
              setOpen(true);
              ensureListLoaded();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              border: '1px solid #d1d5db',
              borderRadius: 8,
              padding: '8px 10px',
              cursor: 'text',
            }}
          >
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search or click to browse…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: 14,
                background: 'transparent',
              }}
              onFocus={() => {
                setOpen(true);
                ensureListLoaded();
              }}
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((v) => {
                  const nv = !v;
                  if (nv) ensureListLoaded();
                  return nv;
                });
              }}
              style={{
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                borderRadius: 6,
                width: 28,
                height: 28,
                display: 'grid',
                placeItems: 'center',
              }}
              aria-label="Toggle list"
            >
              ▾
            </button>
          </div>

          {open && (
            <div
              style={{
                position: 'absolute',
                zIndex: 20,
                left: 0,
                right: 0,
                top: '100%',
                marginTop: 6,
                maxHeight: 380,
                overflow: 'auto',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                background: '#fff',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
              }}
            >
              {loadingList && (
                <div style={{ padding: 12, color: '#6b7280', fontSize: 14 }}>
                  {loadMsg || 'Loading…'}
                </div>
              )}
              {!loadingList && visibleGroups.length === 0 && (
                <div style={{ padding: 12, color: '#6b7280', fontSize: 14 }}>
                  No matches.
                </div>
              )}
              {!loadingList &&
                visibleGroups.map((grp) => (
                  <div key={grp.ministryName}>
                    <div
                      onClick={() => grp.header && pick(grp.header)}
                      style={{
                        padding: '12px 14px',
                        fontWeight: 600,
                        cursor: grp.header ? 'pointer' : 'default',
                        borderTop: '1px solid #f3f4f6',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{grp.ministryName}</span>
                      <span
                        style={{
                          color: '#2563eb',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      >
                        {grp.header ? num(grp.header.rows_count) : ''}
                      </span>
                    </div>
                    {grp.depts.map((d, i) => (
                      <div
                        key={`d-${grp.ministryName}-${i}`}
                        onClick={() => pick(d)}
                        style={{
                          padding: '10px 14px 10px 26px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #f9fafb',
                        }}
                      >
                        <span>Dept — {d.dept || d.label}</span>
                        <span
                          style={{
                            color: '#2563eb',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {num(d.rows_count)}
                        </span>
                      </div>
                    ))}
                    {grp.orgs.map((o, i) => (
                      <div
                        key={`o-${grp.ministryName}-${i}`}
                        onClick={() => pick(o)}
                        style={{
                          padding: '10px 14px 10px 26px',
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          borderTop: '1px solid #f9fafb',
                        }}
                      >
                        <span>Org — {o.org || o.label}</span>
                        <span
                          style={{
                            color: '#2563eb',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {num(o.rows_count)}
                        </span>
                      </div>
                    ))}
                  </div>
                ))}
            </div>
          )}
        </div>

        {selected && (
          <div
            style={{
              marginTop: 6,
              marginBottom: 12,
              fontSize: 14,
              color: '#374151',
            }}
          >
            <div style={{ marginBottom: 6 }}>
              <b>Selected:</b> {selected.path || selected.label || selected.name}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {availabilityLoading && <Spinner size={14} />}
              <b>Available Date Range:</b>{' '}
              {availabilityLoading ? (
                <span style={{ color: '#6b7280' }}>fetching…</span>
              ) : (
                <>
                  <span
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {availability?.min || '-'}
                  </span>{' '}
                  to{' '}
                  <span
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  >
                    {availability?.max || '-'}
                  </span>
                </>
              )}
            </div>
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <label style={{ display: 'block', fontWeight: 600 }}>From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              disabled={availabilityLoading}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #ccc',
                borderRadius: 8,
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600 }}>To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              disabled={availabilityLoading}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #ccc',
                borderRadius: 8,
              }}
            />
          </div>
        </div>

        {/* Actions – THREE buttons now */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
          <button
            disabled={disabledPrice}
            onClick={handlePrice}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #0ea5e9',
              background: '#e0f2fe',
              opacity: disabledPrice ? 0.6 : 1,
              cursor: disabledPrice ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Please wait…' : 'Get Price'}
          </button>

          <button
            disabled={disabledPay}
            onClick={handlePay}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #16a34a',
              background: '#dcfce7',
              opacity: disabledPay ? 0.6 : 1,
              cursor: disabledPay ? 'not-allowed' : 'pointer',
            }}
          >
            Pay & Generate Report
          </button>

          <button
            disabled={disabledQuota}
            onClick={handleQuotaGenerate}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #4f46e5',
              background: '#e0e7ff',
              opacity: disabledQuota ? 0.6 : 1,
              cursor: disabledQuota ? 'not-allowed' : 'pointer',
            }}
          >
            Use Plan Quota
          </button>
        </div>

        {pricing && (
          <div style={{ marginBottom: 12, fontSize: 15 }}>
            Rows: <b>{Number(pricing.rows || 0).toLocaleString()}</b> &nbsp;|&nbsp;
            Amount: ₹ <b>{Number(pricing.amount_rupees || 0).toFixed(2)}</b>
          </div>
        )}

        {downloadUrl && (
          <div style={{ marginTop: 12 }}>
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              Download your report
            </a>
            <div style={{ fontSize: 12, color: '#666' }}>(link valid for 1 hour)</div>
          </div>
        )}

        {message && <div style={{ marginTop: 12, color: '#444' }}>{message}</div>}

        {/* Help / Contact (at end) */}
        <div className="report-help">
          Can’t find your Department/Organisation or need a custom cut? Contact us:
          <div className="contact-actions">
            <a href="tel:+918655654720" className="contact-chip phone" title="Call us">
              <span className="icon">☎</span>
              <span>+91 8655654720</span>
            </a>
            <a href="mailto:info@tendermanagers.com" className="contact-chip email" title="Email us">
              <span className="icon">✉</span>
              <span>info@tendermanagers.com</span>
            </a>
            <a
              className="contact-chip whatsapp"
              href="https://wa.me/8655654720?text=Hi%20Tender%20Managers%2C%20I%27d%20like%20help%20with%20a%20report."
              target="_blank"
              rel="noreferrer"
              title="Chat on WhatsApp"
            >
              <span className="icon">💬</span>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
